import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import Card from "../../components/Card";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import { useToast } from "../../components/Toast/toastHook";
import { GET_CONTACTS_BY_SUBCOMPANY } from "../../graphql/agentQueries";
import { Table, Button, Modal } from "antd";
import { EyeOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import { formatDate } from "../../utils/dateFormat";

const AgentContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [subCompanyInfo, setSubCompanyInfo] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const { companyId, serviceId, subCompanyId } = useParams();
  const location = useLocation();
  const serviceName = location.state?.serviceName || "Service";
  const subCompanyName = location.state?.subCompanyName || "Sub Company";
  const navigate = useNavigate();
  const toast = useToast(4000);

  const { loading, error, data, refetch } = useQuery(GET_CONTACTS_BY_SUBCOMPANY, {
    variables: {
      subCompanyId,
    },
    fetchPolicy: "network-only",
    skip: !subCompanyId,
  });

  useEffect(() => {
    if (data?.getContactsBySubCompany) {
      setContacts(data.getContactsBySubCompany.contacts || []);
      setSubCompanyInfo(data.getContactsBySubCompany);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      toast("error", error.message);
    }
  }, [error]);

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedContact(null);
  };

  const handleGoBack = () => {
    navigate(`/company/${companyId}/agent/services/${serviceId}/sub-companies`, {
      state: { serviceName },
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name) => <span className="font-medium text-black">{name}</span>,
    },
    {
      title: "Position",
      dataIndex: "position",
      key: "position",
      render: (position) => position || "-",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) =>
        email ? (
          <a href={`mailto:${email}`} className="text-primary hover:underline">
            {email}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      render: (phone) =>
        phone ? (
          <a href={`tel:${phone}`} className="text-primary hover:underline">
            {phone}
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Website",
      dataIndex: "url",
      key: "url",
      render: (url) =>
        url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Visit
          </a>
        ) : (
          "-"
        ),
    },
    {
      title: "Actions",
      key: "actions",
      skeleton: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          title="View Details"
        >
          View
        </Button>
      ),
    },
  ];

  const firstLoad = loading && !contacts.length;

  return (
    <div id="agent-contacts">
      <Breadcrumb pageName={`${subCompanyName} - Contacts`} />
      <div className="pb-6">
        {/* Sub Company Info Card */}
        {subCompanyInfo && (
          <Card className="mb-4">
            <div className="p-4">
              <h3 className="font-bold text-lg mb-3">{subCompanyInfo.name}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {subCompanyInfo.address && (
                  <div>
                    <p className="text-gray-500">Address</p>
                    <p>{subCompanyInfo.address}</p>
                  </div>
                )}
                {subCompanyInfo.phone && (
                  <div>
                    <p className="text-gray-500">Phone</p>
                    <a href={`tel:${subCompanyInfo.phone}`} className="text-primary hover:underline">
                      {subCompanyInfo.phone}
                    </a>
                  </div>
                )}
                {subCompanyInfo.url && (
                  <div>
                    <p className="text-gray-500">Website</p>
                    <a
                      href={subCompanyInfo.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary hover:underline"
                    >
                      {subCompanyInfo.url}
                    </a>
                  </div>
                )}
                {subCompanyInfo.serviceAreas && (
                  <div>
                    <p className="text-gray-500">Service Areas</p>
                    <p>{subCompanyInfo.serviceAreas}</p>
                  </div>
                )}
              </div>
              {subCompanyInfo.notes && (
                <div className="mt-3">
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm">{subCompanyInfo.notes}</p>
                </div>
              )}
            </div>
          </Card>
        )}

        <Card>
          <div className="m-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleGoBack}
                  className="mr-2"
                >
                  Back
                </Button>
                <span className="font-bold text-lg">
                  Contacts ({contacts.length})
                </span>
              </div>
            </div>
          </div>
          {error && !contacts.length ? (
            <ErrorState onRetry={() => refetch()} />
          ) : (
            <>
              {error && <ErrorBanner onRetry={() => refetch()} />}
              <Table
                bordered
                rowKey="_id"
                pagination={{
                  position: ["bottomCenter"],
                  pageSize: 10,
                  showSizeChanger: true,
                }}
                {...(firstLoad
                  ? skeletonTableProps(columns)
                  : { columns, dataSource: contacts })}
                locale={{
                  emptyText: (
                    <EmptyState
                      title="No contacts yet"
                      description="Contacts added to this sub-company will appear here."
                    />
                  ),
                }}
              />
            </>
          )}
        </Card>
      </div>

      {/* View Contact Details Modal */}
      <Modal
        title={selectedContact?.name}
        open={isViewModalOpen}
        onCancel={handleCloseViewModal}
        footer={null}
        width={500}
      >
        {selectedContact && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm">Position</p>
                <p className="font-medium">{selectedContact.position || "-"}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm">Phone</p>
                {selectedContact.phone ? (
                  <a
                    href={`tel:${selectedContact.phone}`}
                    className="text-primary hover:underline font-medium"
                  >
                    {selectedContact.phone}
                  </a>
                ) : (
                  <p className="font-medium">-</p>
                )}
              </div>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Email</p>
              {selectedContact.email ? (
                <a
                  href={`mailto:${selectedContact.email}`}
                  className="text-primary hover:underline font-medium"
                >
                  {selectedContact.email}
                </a>
              ) : (
                <p className="font-medium">-</p>
              )}
            </div>
            <div>
              <p className="text-gray-500 text-sm">Address</p>
              <p className="font-medium">{selectedContact.address || "-"}</p>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Website</p>
              {selectedContact.url ? (
                <a
                  href={selectedContact.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline font-medium"
                >
                  {selectedContact.url}
                </a>
              ) : (
                <p className="font-medium">-</p>
              )}
            </div>
            <div>
              <p className="text-gray-500 text-sm">Created Date</p>
              <p className="font-medium">{formatDate(selectedContact.createdAt) || "-"}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AgentContacts;
